import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  IoWalletOutline,
  IoCartOutline,
  IoChevronDownOutline,
  IoDocumentTextOutline,
  IoNotificationsOutline,
  IoBusinessOutline,
  IoHomeOutline,
  IoMenuOutline,
  IoPersonOutline,
  IoCalendarOutline,
  IoClipboardOutline,
  IoChatbubbleOutline,
} from "react-icons/io5";
import { getWallet } from "../api/staff.api";
import { getCart } from "../api/cart.api";
import { getNotifications } from "../api/notifications.api";
import { getStaffId, getStaffName, STORAGE_KEYS, clearSession } from "../utils/storage";
import { logoutStaff } from "../api/auth.api";

const NAV_ITEMS = [
  { label: "Dashboard", path: "/home" },
  { label: "Consultations", path: "/consultations" },
  { label: "Health Risk Assessment", path: "/hra-category" },
  { label: "Lab Tests", path: "/lab-tests" },
];

const getInitials = (name) => {
  if (!name) return "U";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const Header = () => {
  const [walletBalance, setWalletBalance] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const profileRef = useRef(null);
  const staffId = getStaffId();
  const staffName = getStaffName();
  const companyName = localStorage.getItem(STORAGE_KEYS.companyName) || "Company";

  useEffect(() => {
    if (staffId) {
      getWallet(staffId)
        .then((response) => setWalletBalance(response.data.wallet_balance))
        .catch((error) => console.error("Error fetching wallet data:", error));
    }
  }, [staffId]);

  useEffect(() => {
    if (staffId) {
      getNotifications(staffId)
        .then((response) => {
          const list = response.data?.notifications || [];
          const serverUnread = response.data?.unreadCount;
          setNotificationCount(
            typeof serverUnread === "number"
              ? serverUnread
              : list.filter((n) => !n.isRead).length
          );
        })
        .catch(() => setNotificationCount(0));
    }
  }, [staffId]);

  useEffect(() => {
    if (staffId) {
      getCart(staffId)
        .then((response) => {
          if (response.data?.items) setCartCount(response.data.items.length);
        })
        .catch(() => setCartCount(0));
    }
  }, [staffId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleNavigation = (path, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsProfileOpen(false);
    navigate(path);
  };

  const isActive = (path) => {
    if (path === "/home") return location.pathname === "/home";
    return location.pathname.startsWith(path);
  };

  const IconWrapper = ({ children }) => (
    <div className="bg-surface-low rounded-full p-1.5 flex items-center justify-center">
      {children}
    </div>
  );

  const MOBILE_NAV_ITEMS = [
    { label: "Home", path: "/home", icon: IoHomeOutline },
    { label: "Consult", path: "/consultations", icon: IoMenuOutline },
    { label: "Records", path: "/medicalrecord", icon: IoDocumentTextOutline },
    { label: "HRA", path: "/hra-category", icon: IoClipboardOutline },
    { label: "Bookings", path: "/mybookings", icon: IoCalendarOutline },
  ];

  return (
    <>
      <header className="bg-surface-lowest border-b border-surface-container sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex justify-between items-center px-4 sm:px-6 h-16 gap-4">
          {/* Logo + company pill */}
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/home" className="flex items-center gap-2 no-underline shrink-0">
              <img src="/logo.png" alt="Logo" className="w-9 h-9 object-contain" />
              <span className="font-bold text-lg text-primary hidden sm:inline">Elthium Health</span>
            </Link>
            {/* <div className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-surface-container bg-surface-low text-xs font-medium text-on-primary-fixed-var max-w-[180px]">
              <IoBusinessOutline className="text-primary shrink-0" size={14} />
              <span className="truncate">{companyName}</span>
              <IoChevronDownOutline size={12} className="text-on-surface-variant shrink-0" />
            </div> */}
          </div>

          {/* Center nav — desktop */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_ITEMS.map(({ label, path }) => (
              <button
                key={path}
                onClick={() => handleNavigation(path)}
                className={`text-sm font-medium pb-1 transition-colors ${
                  isActive(path)
                    ? "text-primary border-b-2 border-primary"
                    : "text-on-primary-fixed-var hover:text-primary"
                }`}
              >
                {label}
              </button>
            ))}
          </nav>

          {/* Right utilities */}
          <div className="flex items-center gap-3 sm:gap-4">
            {walletBalance !== null && (
              <button
                onClick={() => handleNavigation("/wallet")}
                className="hidden sm:flex items-center gap-1.5 text-on-primary-fixed-var bg-surface-low px-3 py-1.5 rounded-full border border-surface-container hover:bg-surface-container transition text-sm font-medium"
                title="Go to Wallet"
              >
                <IoWalletOutline size={18} className="text-primary" />
                ₹{Number(walletBalance).toLocaleString("en-IN")}
              </button>
            )}

            <button
              onClick={() => handleNavigation("/chat")}
              className={`relative p-1 transition ${
                location.pathname.startsWith("/chat")
                  ? "text-primary"
                  : "text-on-primary-fixed-var hover:text-primary"
              }`}
              aria-label="Doctor chat"
            >
              <IoChatbubbleOutline size={22} />
            </button>

            <button
              onClick={() => handleNavigation("/cart")}
              className="relative p-1 text-on-primary-fixed-var hover:text-primary transition"
              aria-label="Cart"
            >
              <IoCartOutline size={22} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-error text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>

            <button
              onClick={() => handleNavigation("/notification")}
              className="relative p-1 text-on-primary-fixed-var hover:text-primary transition"
              aria-label="Notifications"
            >
              <IoNotificationsOutline size={22} />
              {notificationCount > 0 && (
                <span className="absolute top-0 right-0 min-w-[8px] h-2 px-0.5 bg-error rounded-full flex items-center justify-center">
                  {notificationCount > 9 ? (
                    <span className="sr-only">{notificationCount} notifications</span>
                  ) : null}
                </span>
              )}
            </button>

            <div className="relative" ref={profileRef}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsProfileOpen(!isProfileOpen);
                }}
                className="w-9 h-9 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center hover:bg-primary-container transition"
                aria-label="Profile menu"
              >
                {getInitials(staffName)}
              </button>

              {isProfileOpen && (
                <div className="absolute top-full right-0 mt-2 w-52 bg-surface-lowest rounded-xl shadow-lg border border-surface-container py-1 z-50">
                  <button
                    onClick={(e) => handleNavigation("/myprofile", e)}
                    className="w-full px-4 py-2.5 hover:bg-surface-low flex items-center gap-2 text-sm text-left"
                  >
                    <IoPersonOutline className="text-primary" size={16} />
                    Profile
                  </button>
                  <button
                    onClick={(e) => handleNavigation("/mybookings", e)}
                    className="w-full px-4 py-2.5 hover:bg-surface-low flex items-center gap-2 text-sm text-left"
                  >
                    <IoCalendarOutline className="text-primary" size={16} />
                    My Bookings
                  </button>
                  <button
                    onClick={(e) => handleNavigation("/medicalrecord", e)}
                    className="w-full px-4 py-2.5 hover:bg-surface-low flex items-center gap-2 text-sm text-left"
                  >
                    <IoDocumentTextOutline className="text-primary" size={16} />
                    Medical Records
                  </button>
                  <div className="border-t border-surface-container my-1" />
                  <button
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsProfileOpen(false);
                      try {
                        await logoutStaff();
                      } catch {
                        /* token may already be invalid */
                      } finally {
                        clearSession();
                        navigate("/");
                      }
                    }}
                    className="w-full px-4 py-2.5 hover:bg-red-50 flex items-center gap-2 text-sm text-left text-red-600 font-medium"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile bottom nav */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-surface-lowest shadow-inner border-t border-surface-container z-40">
        <div className="flex items-center py-1.5 px-1">
          {MOBILE_NAV_ITEMS.map(({ label, path, icon: NavIcon }) => (
            <button
              key={path}
              onClick={() => handleNavigation(path)}
              className={`flex flex-1 min-w-0 flex-col items-center gap-0.5 text-[10px] ${
                isActive(path) ? "text-primary font-medium" : "text-on-primary-fixed-var"
              }`}
            >
              <IconWrapper>
                <NavIcon size={18} />
              </IconWrapper>
              {label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default Header;
